import React, { useEffect, useState } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Copy, Receipt, Store } from "lucide-react";
import { api, money } from "@/lib/api";

function CopyRow({ label, value, testid }) {
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(`${label} copied`);
    } catch {
      try {
        const ta = document.createElement("textarea");
        ta.value = value;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
        toast.success(`${label} copied`);
      } catch {
        toast.error("Could not copy — please copy it manually");
      }
    }
  };
  return (
    <button onClick={copy} className="w-full flex items-center justify-between py-2.5 active:scale-[0.98] transition-transform" data-testid={testid}>
      <span className="text-sm text-[#6B8375]">{label}</span>
      <span className="flex items-center gap-2 font-bold text-[#122117]">
        {value} <Copy size={14} className="text-[#0B7A3B]" />
      </span>
    </button>
  );
}

export default function OrderSuccess() {
  const { orderId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [order, setOrder] = useState(location.state?.order || null);

  useEffect(() => {
    if (!order) {
      api.get(`/orders/${orderId}`).then(({ data }) => setOrder(data)).catch(() => navigate("/orders"));
    }
  }, [order, orderId, navigate]);

  if (!order) return <div className="min-h-screen flex items-center justify-center text-[#6B8375]">Loading…</div>;
  const bank = order.bank_details || {};

  return (
    <div className="max-w-md mx-auto min-h-screen bg-[#F5F8F6] pb-10">
      <div className="bg-gradient-to-br from-[#095F2E] to-[#0B7A3B] px-6 pt-16 pb-12 rounded-b-[32px] text-center">
        <div className="w-20 h-20 rounded-full bg-white/15 flex items-center justify-center mx-auto pm-pop">
          <CheckCircle2 size={44} className="text-white" />
        </div>
        <h1 className="text-white text-2xl font-extrabold mt-4">Order placed!</h1>
        <p className="text-white/80 mt-1">Order #{order.order_number} · {money(order.grand_total)}</p>
      </div>

      <div className="px-4 -mt-6 space-y-4">
        <div className="bg-white rounded-[20px] p-4 shadow-[0_8px_30px_rgba(11,122,59,0.08)]" data-testid="bank-details">
          <h2 className="font-extrabold text-[#122117] mb-1">Pay by bank transfer</h2>
          <p className="text-xs text-[#6B8375] mb-2">Tap any detail to copy. Use the reference so we can match your payment.</p>
          <div className="divide-y divide-[#F5F8F6]">
            <CopyRow label="Bank" value={bank.bank} testid="copy-bank" />
            <CopyRow label="Account name" value={bank.account_name} testid="copy-accname" />
            <CopyRow label="Sort code" value={bank.sort_code} testid="copy-sortcode" />
            <CopyRow label="Account number" value={bank.account_number} testid="copy-accnum" />
            <CopyRow label="Reference" value={bank.reference} testid="copy-reference" />
          </div>
        </div>

        <div className="bg-white rounded-[20px] p-4">
          <h2 className="font-extrabold text-[#122117] mb-3">Your items</h2>
          <div className="space-y-2">
            {order.items.map((i) => (
              <div key={i.product_id} className="flex justify-between text-sm">
                <span className="text-[#122117]">{i.name} × {i.quantity}</span>
                <span className="font-semibold">{money(i.price * i.quantity)}</span>
              </div>
            ))}
          </div>
          <div className="h-px bg-[#F5F8F6] my-3" />
          <div className="flex justify-between text-sm text-[#6B8375]"><span>Subtotal</span><span>{money(order.subtotal)}</span></div>
          <div className="flex justify-between text-sm text-[#6B8375] mt-1"><span>{order.charge_label}</span><span>{money(order.charge)}</span></div>
          <div className="flex justify-between font-extrabold text-lg mt-2"><span>Total</span><span className="text-[#0B7A3B]">{money(order.grand_total)}</span></div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <button onClick={() => navigate("/orders")} className="flex items-center justify-center gap-2 bg-[#DDF0E5] text-[#0B7A3B] font-bold rounded-full py-3.5 active:scale-95 transition-transform" data-testid="go-orders">
            <Receipt size={18} /> My orders
          </button>
          <button onClick={() => navigate("/market")} className="flex items-center justify-center gap-2 bg-[#0B7A3B] text-white font-bold rounded-full py-3.5 active:scale-95 transition-transform" data-testid="back-market">
            <Store size={18} /> Market
          </button>
        </div>
      </div>
    </div>
  );
}
